import PropTypes from 'prop-types';
import { createContext, useMemo, useState, useEffect } from 'react';
// @mui
import { CssBaseline, useMediaQuery } from '@mui/material';
import {
  createTheme,
  ThemeProvider as MUIThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles';
// hooks
import useSettings from '../hooks/useSettings';
//
import palette from './palette';
import typography from './typography';
import breakpoints from './breakpoints';
import componentsOverride from './overrides';
import shadows, { customShadows } from './shadows';

// ----------------------------------------------------------------------

// Création du contexte pour gérer le mode
export const ThemeContext = createContext();

ThemeProvider.propTypes = {
  children: PropTypes.node,
};

export default function ThemeProvider({ children }) {
  const { themeDirection } = useSettings();
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)'); // Détection système

  // Récupérer le mode stocké (par défaut "system")
  const storedThemeMode = localStorage.getItem('themeMode') || 'system';
  const [themeMode, setThemeMode] = useState(storedThemeMode);

  // Déterminer le mode à appliquer
  const appliedThemeMode =
    themeMode === 'system' ? (prefersDarkMode ? 'dark' : 'light') : themeMode;

  useEffect(() => {
    localStorage.setItem('themeMode', themeMode); // Sauvegarde de la préférence
  }, [themeMode]);

  // Fonction pour changer le mode
  const changeThemeMode = (mode) => {
    setThemeMode(mode);
  };

  const themeOptions = useMemo(
    () => ({
      palette: appliedThemeMode === 'light' ? palette.light : palette.dark,
      typography,
      breakpoints,
      shape: { borderRadius: 25 },
      direction: themeDirection,
      shadows: appliedThemeMode === 'light' ? shadows.light : shadows.dark,
      customShadows: appliedThemeMode === 'light' ? customShadows.light : customShadows.dark,
  
      components: {
        MuiButton: {
          styleOverrides: {
            outlined: {
              color: 'inherit', // Text color matches the outline color
            },
            contained: {
              color: '#fff', // White text for contained buttons
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              color: '#fff', // White text for chips
            },
          },
        },
      },
    }),
    [appliedThemeMode, themeDirection]
  );

  const theme = createTheme(themeOptions);
  theme.components = componentsOverride(theme);

  return (
    <ThemeContext.Provider value={{ themeMode, appliedThemeMode, changeThemeMode }}>
      <StyledEngineProvider injectFirst>
        <MUIThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </MUIThemeProvider>
      </StyledEngineProvider>
    </ThemeContext.Provider>
  );
}